import { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import LinearProgress from "@mui/material/LinearProgress";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import {
  MISSION_DOCUMENT_ACCEPT,
  uploadAndLinkMissionDocuments,
  uploadAndLinkResourceRequestDocuments,
} from "utils/missionDocuments";

function MissionDocumentUploadDialog({
  open,
  onClose,
  missionId,
  requestId,
  entityId,
  idToken,
  onSuccess,
  title = "Upload mission documents",
  linkStepLabel = "Link to mission",
  uploadAndLink = uploadAndLinkMissionDocuments,
  fileInputId = "mission-detail-document-file",
}) {
  const resolvedEntityId = entityId ?? requestId ?? missionId ?? "";
  const steps = useMemo(
    () => ["Select documents", "Upload files", linkStepLabel],
    [linkStepLabel],
  );

  const [activeStep, setActiveStep] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadLabel, setUploadLabel] = useState("");
  const [uploadPercent, setUploadPercent] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setActiveStep(0);
    setSelectedFiles([]);
    setUploadLabel("");
    setUploadPercent(0);
    setBusy(false);
    setError("");
  };

  useEffect(() => {
    if (!open) reset();
  }, [open]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setSelectedFiles((prev) => [...prev, ...files]);
    setError("");
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const runUploadAndLink = async () => {
    if (!selectedFiles.length || !resolvedEntityId || !idToken) return;
    setBusy(true);
    setError("");
    setActiveStep(1);
    setUploadPercent(0);
    setUploadLabel(selectedFiles[0]?.name ?? "");

    try {
      const result = await uploadAndLink(
        resolvedEntityId,
        selectedFiles,
        idToken,
        {
          onFileProgress: (fileName, percent, index, total) => {
            setUploadLabel(fileName);
            setUploadPercent(percent);
            if (percent >= 100 && index < total - 1) {
              setUploadPercent(0);
            }
          },
          onLinking: () => setActiveStep(2),
        },
      );
      onSuccess?.(result);
      onClose?.();
    } catch (err) {
      setError(err.message || "Failed to upload and link documents");
      setActiveStep(0);
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => {
    if (busy) return;
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3, mt: 1 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error ? (
          <SoftTypography
            variant="caption"
            color="error"
            display="block"
            mb={2}
          >
            {error}
          </SoftTypography>
        ) : null}

        {activeStep === 0 && (
          <SoftBox>
            <input
              type="file"
              accept={MISSION_DOCUMENT_ACCEPT}
              multiple
              onChange={handleFileChange}
              style={{ display: "none" }}
              id={fileInputId}
              disabled={busy}
            />
            <label htmlFor={fileInputId}>
              <SoftButton
                variant="outlined"
                color="secondary"
                component="span"
                disabled={busy}
                startIcon={<Icon>upload_file</Icon>}
              >
                Choose files
              </SoftButton>
            </label>
            <SoftTypography
              variant="caption"
              color="secondary"
              display="block"
              mt={0.5}
            >
              Select one or more files. They will be linked together in a single
              action.
            </SoftTypography>
            {selectedFiles.length > 0 ? (
              <SoftBox mt={2} display="flex" flexDirection="column" gap={1}>
                {selectedFiles.map((file, idx) => (
                  <SoftBox
                    key={`${file.name}-${file.size}-${idx}`}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    p={1}
                    sx={(theme) => ({
                      borderRadius: "0.5rem",
                      border: `1px dashed ${theme.palette.divider}`,
                    })}
                  >
                    <SoftTypography variant="caption">
                      {file.name} ({Math.round(file.size / 1024)} KB)
                    </SoftTypography>
                    <SoftButton
                      variant="text"
                      color="error"
                      size="small"
                      onClick={() => removeFile(idx)}
                      disabled={busy}
                    >
                      Remove
                    </SoftButton>
                  </SoftBox>
                ))}
              </SoftBox>
            ) : null}
          </SoftBox>
        )}

        {activeStep === 1 && (
          <SoftBox>
            <SoftTypography variant="body2" mb={1}>
              Uploading {uploadLabel}
              {selectedFiles.length > 1
                ? ` (of ${selectedFiles.length} files)`
                : ""}
              …
            </SoftTypography>
            <LinearProgress variant="determinate" value={uploadPercent} />
            <SoftTypography variant="caption" color="secondary" mt={0.5}>
              {uploadPercent}%
            </SoftTypography>
          </SoftBox>
        )}

        {activeStep === 2 && (
          <SoftBox display="flex" alignItems="center" gap={1}>
            <CircularProgress size={22} />
            <SoftTypography variant="body2">
              Linking {selectedFiles.length} document
              {selectedFiles.length === 1 ? "" : "s"} to{" "}
              {linkStepLabel.replace(/^Link to /i, "")}…
            </SoftTypography>
          </SoftBox>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <SoftButton
          variant="text"
          color="secondary"
          onClick={handleClose}
          disabled={busy}
        >
          Cancel
        </SoftButton>
        {activeStep === 0 && (
          <SoftButton
            variant="gradient"
            color="info"
            disabled={busy || selectedFiles.length === 0}
            onClick={runUploadAndLink}
          >
            Upload &amp; link
          </SoftButton>
        )}
      </DialogActions>
    </Dialog>
  );
}

MissionDocumentUploadDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  missionId: PropTypes.string,
  requestId: PropTypes.string,
  entityId: PropTypes.string,
  idToken: PropTypes.string,
  onSuccess: PropTypes.func,
  title: PropTypes.string,
  linkStepLabel: PropTypes.string,
  uploadAndLink: PropTypes.func,
  fileInputId: PropTypes.string,
};

export default MissionDocumentUploadDialog;
